// Inventory types
export interface InventoryItem {
  id: number;
  name: string;
  code: string | null;
  description: string | null;
  unit: string;
  minimum_stock: string;
  cost_per_unit: number;
  supplier: string | null;
  current_stock: string;
  last_restocked: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface InventoryTransaction {
  id: number;
  ingredient_id: number;
  transaction_type: TransactionType;
  quantity: string;
  unit_cost: number | null;
  reference_id: number | null;
  reference_type: string | null;
  notes: string | null;
  current_stock_after: string;
  created_by: number;
  created_by_name: string;
  created_at: string;
  ingredient_name: string;
  ingredient_unit: string;
}

export interface StockAdjustment {
  id: number;
  adjustment_date: string;
  reason: string;
  notes: string | null;
  created_by: number;
  created_by_name: string;
  transactions: InventoryTransaction[];
}

export interface LowStockAlert {
  ingredient_id: number;
  ingredient_name: string;
  current_stock: string;
  minimum_stock: string;
  unit: string;
  percentage: string;
}

export interface InventorySummary {
  total_ingredients: number;
  active_ingredients: number;
  low_stock_count: number;
  total_value: number;
  recent_transactions: InventoryTransaction[];
  low_stock_alerts: LowStockAlert[];
}

// Enum for transaction types
export enum TransactionType {
  Purchase = "purchase",
  Usage = "usage",
  Adjustment = "adjustment",
  Waste = "waste",
  Transfer = "transfer",
}

// Helper functions for inventory
export const formatQuantity = (quantity: string, unit: string): string => {
  return `${quantity} ${unit}`;
};

export const getStockStatus = (
  current: number,
  minimum: number,
): "critical" | "low" | "normal" => {
  const percentage = (current / minimum) * 100;

  if (percentage <= 25) {
    return "critical";
  } else if (percentage <= 75) {
    return "low";
  } else {
    return "normal";
  }
};

export const getStatusColor = (
  status: "critical" | "low" | "normal",
): string => {
  switch (status) {
    case "critical":
      return "text-red-600 dark:text-red-400";
    case "low":
      return "text-yellow-600 dark:text-yellow-400";
    case "normal":
      return "text-green-600 dark:text-green-400";
    default:
      return "text-gray-600 dark:text-gray-400";
  }
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount / 100); // Assuming amount is in cents
};

export const getTransactionTypeLabel = (type: TransactionType): string => {
  switch (type) {
    case TransactionType.Purchase:
      return "Purchase";
    case TransactionType.Usage:
      return "Usage";
    case TransactionType.Adjustment:
      return "Adjustment";
    case TransactionType.Waste:
      return "Waste";
    case TransactionType.Transfer:
      return "Transfer";
    default:
      return type;
  }
};
